import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from 'axios';
import './Career.css';

const Career = ({url, jobId}) => {
  const [vacancy, setVacancy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applicationCount, setApplicationCount] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    mobileNo: "",
    jobId: jobId || "",
    city: "",
    state: "",
    pinCode: "",
    linkedinUrl: "",
    tenthPercentage: "",
    twelfthPercentage: "",
    degree: "",
    degreeCgpa: "",
    resume: null,
    coverLetter: null,
    candidateMessage: "",
  });

  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Fetch vacancy details
  useEffect(() => {
    const fetchVacancy = async () => {
      if (!jobId) {
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${url}/api/vacancy/get/${jobId}`);
        if (response.data.success) {
          setVacancy(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching vacancy:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchVacancy();
  }, [jobId, url]);

  // Fetch application count
  useEffect(() => {
    const fetchApplicationCount = async () => {
      if (!jobId) return;
      try {
        const response = await axios.get(`${url}/api/cv/count/${jobId}`);
        if (response.data.success && response.data.count !== null) {
          setApplicationCount(response.data.count);
        } else {
          setApplicationCount(null);
        }
      } catch (error) {
        console.error('Error fetching application count:', error);
        setApplicationCount(null);
      }
    };
    fetchApplicationCount();
  }, [jobId, url]);

  // handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // handle file input
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData({ ...formData, [name]: files?.[0] || null });
  };

  // handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!formData.resume) {
      setMessage("Please upload your resume before submitting. ❌");
      return;
    }

    const wordCount = (formData.candidateMessage || "").trim().split(/\s+/).filter(Boolean).length;
    if (formData.candidateMessage.trim() && wordCount > 100) {
      setMessage("Message must be at most 100 words. ❌");
      return;
    }

    setSubmitting(true);

    // Use vacancy.jobId if available, otherwise fall back to formData.jobId
    const jobIdToSubmit = vacancy?.jobId || formData.jobId;

    const data = new FormData();
    data.append("firstName", formData.firstName);
    data.append("lastName", formData.lastName);
    data.append("email", formData.email);
    data.append("mobileNo", formData.mobileNo);
    data.append("jobId", jobIdToSubmit);
    if (formData.address) data.append("address", formData.address);
    if (formData.city) data.append("city", formData.city);
    if (formData.state) data.append("state", formData.state);
    if (formData.tenthPercentage !== "") data.append("tenthPercentage", formData.tenthPercentage);
    if (formData.twelfthPercentage !== "") data.append("twelfthPercentage", formData.twelfthPercentage);
    if (formData.degree) data.append("degree", formData.degree);
    if (formData.degreeCgpa !== "") data.append("degreeCgpa", formData.degreeCgpa);
    if (formData.resume) data.append("resume", formData.resume);
    if (formData.coverLetter) data.append("coverLetter", formData.coverLetter);
    if (formData.candidateMessage.trim()) data.append("candidateMessage", formData.candidateMessage.trim());

    try {
      const result = await axios.post(`${url}/api/cv/add`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Check if response indicates success
      if (result.data && result.data.success) {
        setIsSubmitted(true);
        setMessage("Application submitted successfully");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          address: "",
          mobileNo: "",
          jobId: jobId || "",
          city: "",
          state: "",
          pinCode: "",
          linkedinUrl: "",
          tenthPercentage: "",
          twelfthPercentage: "",
          degree: "",
          degreeCgpa: "",
          resume: null,
          coverLetter: null,
          candidateMessage: "",
        });
        const resumeInput = document.getElementById('resume');
        const coverInput = document.getElementById('coverLetter');
        if (resumeInput) resumeInput.value = '';
        if (coverInput) coverInput.value = '';
        if (jobIdToSubmit && url) {
          try {
            const countResponse = await axios.get(`${url}/api/cv/count/${jobIdToSubmit}`);
            if (countResponse.data?.success && countResponse.data.count !== null) {
              setApplicationCount(countResponse.data.count);
            }
          } catch (err) {
            console.error('Error refreshing application count:', err);
          }
        }
      } else {
        // Handle case where success is false but no error was thrown
        const errorMessage = result.data?.message || "Failed to submit application";
        setMessage(`${errorMessage} ❌`);
      }
    } catch (error) {
      console.error('Application submission error:', error);
      
      // Handle axios error responses (400, 500, etc.)
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || 
                            error.response.data?.error || 
                            `Server error (${error.response.status})`;
        setMessage(`${errorMessage} ❌`);
      } else if (error.request) {
        // Request was made but no response received
        setMessage("Network error. Please check your connection and try again. ❌");
      } else {
        // Something else happened
        const errorMessage = error.message || "Error submitting application";
        setMessage(`${errorMessage} ❌`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="career-loading">Loading job details...</div>;
  }

  return (
    <div className="career-apply-page">
      {/* Vacancy Details Section */}
      {vacancy && (
        <div className="vacancy-details-section">
          <div className="vacancy-header">
            <div className="vacancy-title-group">
              <h1 className="vacancy-title">{vacancy.jobTitle}</h1>
              {vacancy.industry && (
                <span className="vacancy-industry-badge">{vacancy.industry.name}</span>
              )}
              {vacancy.client && (
                <span className="vacancy-client-badge">🏢 {vacancy.client.name}</span>
              )}
            </div>
            <span className="vacancy-job-id">Job ID: #{vacancy.jobId}</span>
          </div>

          {/* Application Count */}
          {applicationCount !== null && applicationCount > 0 && (
            <div className="application-count-badge">
              <span className="count-icon">👥</span>
              <span className="count-text">
                {applicationCount} {applicationCount === 1 ? 'person' : 'people'} clicked apply
              </span>
            </div>
          )}

          <div className="vacancy-meta">
            {vacancy.location && (vacancy.location.city || vacancy.location.state) && (
              <span className="meta-item">
                <span className="meta-icon">📍</span>
                {vacancy.location.city}{vacancy.location.city && vacancy.location.state ? ', ' : ''}{vacancy.location.state}
                {vacancy.location.isRemote && <span className="remote-badge">Remote</span>}
              </span>
            )}
            {vacancy.employmentType && (
              <span className="meta-item">
                <span className="meta-icon">💼</span>
                {vacancy.employmentType}
              </span>
            )}
            {vacancy.experienceLevel && (
              <span className="meta-item">
                <span className="meta-icon">⭐</span>
                {vacancy.experienceLevel}
              </span>
            )}
          </div>

          {vacancy.salary && (vacancy.salary.min || vacancy.salary.max) && (
            <div className="vacancy-salary-display">
              <span className="salary-label">Salary:</span>
              <span className="salary-amount">
                {vacancy.salary.min && vacancy.salary.max 
                  ? `₹${vacancy.salary.min.toLocaleString()} - ₹${vacancy.salary.max.toLocaleString()}`
                  : vacancy.salary.min 
                    ? `₹${vacancy.salary.min.toLocaleString()}+`
                    : `Up to ₹${vacancy.salary.max.toLocaleString()}`
                }
              </span>
              {vacancy.salary.isNegotiable && <span className="negotiable-badge">Negotiable</span>}
            </div>
          )}

          {vacancy.skills && vacancy.skills.length > 0 && (
            <div className="vacancy-skills-display">
              <strong>Required Skills:</strong>
              <div className="skills-tags">
                {vacancy.skills.map((skill, idx) => (
                  <span key={idx} className="skill-tag">{skill}</span>
                ))}
              </div>
            </div>
          )}

          <div className="vacancy-description-section">
            <h3>Job Description</h3>
            <p>{vacancy.description}</p>
          </div>

          <div className="vacancy-qualification-section">
            <h3>Qualifications Required</h3>
            <p>{vacancy.qualification}</p>
          </div>

          {vacancy.applicationDeadline && (
            <div className="application-deadline">
              <strong>Application Deadline:</strong> {new Date(vacancy.applicationDeadline).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </div>
          )}
        </div>
      )}

      {/* Application Form Section */}
      <div className="application-form-section">
        {isSubmitted ? (
          <div className="success-state">
            <div className="success-state-icon">✓</div>
            <h2 className="success-state-title">Application Submitted Successfully</h2>
            <p className="success-state-message">
              Thank you for applying. We will review your application and get back to you soon.
            </p>
            <Link to="/careers" className="success-state-link">Browse more job openings</Link>
          </div>
        ) : (
          <>
        <div className="form-header">
          <h2>Application Form</h2>
          <p>Fill in your details to apply for this position</p>
        </div>

        <form onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
          {/* Personal Information */}
          <div className="form-section">
            <h3 className="section-title">Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter your last name"
                />
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="form-section">
            <h3 className="section-title">Contact Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="mobileNo">Mobile Number *</label>
                <input
                  type="tel"
                  id="mobileNo"
                  name="mobileNo"
                  required
                  value={formData.mobileNo}
                  onChange={handleChange}
                  placeholder="+91 1234567890"
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="address">Address</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Your complete address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter your city"
                />
              </div>

              <div className="form-group">
                <label htmlFor="state">State</label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                >
                  <option value="" disabled>Select your state</option>
                  <option value="Andhra Pradesh">Andhra Pradesh</option>
                  <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                  <option value="Assam">Assam</option>
                  <option value="Bihar">Bihar</option>
                  <option value="Chhattisgarh">Chhattisgarh</option>
                  <option value="Goa">Goa</option>
                  <option value="Gujarat">Gujarat</option>
                  <option value="Haryana">Haryana</option>
                  <option value="Himachal Pradesh">Himachal Pradesh</option>
                  <option value="Jharkhand">Jharkhand</option>
                  <option value="Karnataka">Karnataka</option>
                  <option value="Kerala">Kerala</option>
                  <option value="Madhya Pradesh">Madhya Pradesh</option>
                  <option value="Maharashtra">Maharashtra</option>
                  <option value="Manipur">Manipur</option>
                  <option value="Meghalaya">Meghalaya</option>
                  <option value="Mizoram">Mizoram</option>
                  <option value="Nagaland">Nagaland</option>
                  <option value="Odisha">Odisha</option>
                  <option value="Punjab">Punjab</option>
                  <option value="Rajasthan">Rajasthan</option>
                  <option value="Sikkim">Sikkim</option>
                  <option value="Tamil Nadu">Tamil Nadu</option>
                  <option value="Telangana">Telangana</option>
                  <option value="Tripura">Tripura</option>
                  <option value="Uttar Pradesh">Uttar Pradesh</option>
                  <option value="Uttarakhand">Uttarakhand</option>
                  <option value="West Bengal">West Bengal</option>
                  <option value="Delhi">Delhi</option>
                  <option value="Jammu and Kashmir">Jammu and Kashmir</option>
                  <option value="Ladakh">Ladakh</option>
                  <option value="Puducherry">Puducherry</option>
                  <option value="Chandigarh">Chandigarh</option>
                  <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                  <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                  <option value="Lakshadweep">Lakshadweep</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="pinCode">Pin Code *</label>
                <input
                  type="text"
                  id="pinCode"
                  name="pinCode"
                  required
                  value={formData.pinCode}
                  onChange={handleChange}
                  placeholder="e.g., 201301"
                  pattern="[0-9]{6}"
                  maxLength="6"
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="linkedinUrl">LinkedIn Profile URL</label>
                <input
                  type="url"
                  id="linkedinUrl"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleChange}
                  placeholder="https://www.linkedin.com/in/your-profile"
                />
              </div>
            </div>
          </div>

          {/* Education Details */}
          <div className="form-section">
            <h3 className="section-title">Education Details</h3>
            <p className="section-hint">Optional – helps us better match your profile to the role</p>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="tenthPercentage">10th Percentage</label>
                <input
                  type="number"
                  id="tenthPercentage"
                  name="tenthPercentage"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.tenthPercentage}
                  onChange={handleChange}
                  placeholder="e.g., 85.5"
                />
              </div>

              <div className="form-group">
                <label htmlFor="twelfthPercentage">12th/Diploma Percentage</label>
                <input
                  type="number"
                  id="twelfthPercentage"
                  name="twelfthPercentage"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.twelfthPercentage}
                  onChange={handleChange}
                  placeholder="e.g., 80.0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="degree">Degree</label>
                <select
                  id="degree"
                  name="degree"
                  value={formData.degree}
                  onChange={handleChange}
                >
                  <option value="">Select your degree (optional)</option>
                  <option value="B.Tech CSE">B.Tech CSE</option>
                  <option value="B.Tech IT">B.Tech IT</option>
                  <option value="B.Tech Other Branches">B.Tech Other Branches</option>
                  <option value="MCA">MCA</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="degreeCgpa">Degree CGPA</label>
                <input
                  type="number"
                  id="degreeCgpa"
                  name="degreeCgpa"
                  min="0"
                  max="10"
                  step="0.01"
                  value={formData.degreeCgpa}
                  onChange={handleChange}
                  placeholder="e.g., 8.5"
                />
              </div>
            </div>
          </div>

          {/* Message to Recruiter */}
          <div className="form-section">
            <h3 className="section-title">Message (optional)</h3>
            <p className="section-hint">Add a brief message for the recruiter – up to 100 words</p>
            <div className="form-group full-width">
              <textarea
                id="candidateMessage"
                name="candidateMessage"
                value={formData.candidateMessage}
                onChange={handleChange}
                placeholder="e.g., Why you're interested in this role, your availability, or any relevant details..."
                className="form-textarea"
                rows={4}
                maxLength={650}
              />
              <span className="word-count">
                {(formData.candidateMessage || "").trim().split(/\s+/).filter(Boolean).length} / 100 words
              </span>
            </div>
          </div>

          {/* Resume & Cover Letter Upload */}
          <div className="form-section">
            <h3 className="section-title">Resume & Cover Letter</h3>
            <div className="form-grid">
              <div className="form-group full-width">
                <div className="file-upload-area">
                  <label htmlFor="resume" className="file-upload-label">
                    <div className="upload-icon">📄</div>
                    <div className="upload-text">
                      {formData.resume ? formData.resume.name : 'Resume * – Click to upload'}
                    </div>
                    <div className="upload-hint">PDF, DOC, or DOCX (Max 5MB)</div>
                  </label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                </div>
              </div>
              <div className="form-group full-width">
                <div className="file-upload-area">
                  <label htmlFor="coverLetter" className="file-upload-label">
                    <div className="upload-icon">📝</div>
                    <div className="upload-text">
                      {formData.coverLetter ? formData.coverLetter.name : 'Cover Letter (optional) – Click to upload'}
                    </div>
                    <div className="upload-hint">PDF, DOC, or DOCX (Max 5MB)</div>
                  </label>
                  <input
                    type="file"
                    id="coverLetter"
                    name="coverLetter"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>

          {message && (
            <div className={`message ${message.includes('❌') ? 'error' : 'success'}`} role="alert">
              <strong>{message.includes('❌') ? 'Error:' : 'Success:'}</strong> {message.replace('❌', '').trim()}
            </div>
          )}
        </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Career;
